<template>
  <div class="flex flex-col gap-6">
    <component
      v-for="(block, index) in content"
      :key="`block-${index}-${block._uid || index}`"
      :is="getComponentName(block.component || block.component_type)"
      v-bind="{ ...getComponentProps(block), ...getEditableAttributes(block) }"
    />
    <div v-if="content.length === 0" class="text-center text-[#63615E] py-8">
      <p>No content blocks found.</p>
    </div>
  </div>
</template>

<script>
import { convertStoryblokRichTextToHtml } from '@/utils/storyblokRichText';
import { getStoryblokEditableAttributes } from '@/utils/storyblok-editable';
import ContentTitle from './ContentComponents/Title.vue';
import ContentSubtitle from './ContentComponents/Subtitle.vue';
import ContentText from './ContentComponents/Text.vue';
import ContentHeader from './ContentComponents/Header.vue';
import ContentQuote from './ContentComponents/Quote.vue';
import ContentImage from './ContentComponents/Image.vue';
import ContentCarousel from './ContentComponents/Carousel.vue';
import ContentEmbed from './ContentComponents/Embed.vue';

export default {
  name: 'StoryblokRenderer',
  components: {
    ContentTitle,
    ContentSubtitle,
    ContentText,
    ContentHeader,
    ContentQuote,
    ContentImage,
    ContentCarousel,
    ContentEmbed,
  },
  props: {
    content: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    hasUnrenderedBlocks() {
      return this.content.some(block => {
        const componentName = this.getComponentName(block.component || block.component_type);
        return !componentName;
      });
    },
  },
  methods: {
    getComponentName(componentType) {
      if (!componentType) {
        console.warn('Component type is missing');
        return null;
      }

      // Handle Storyblok component names (can be with or without prefix)
      const normalizedType = componentType.toLowerCase().replace(/^church-update-/, '');

      const componentMap = {
        title: 'ContentTitle',
        subtitle: 'ContentSubtitle',
        text: 'ContentText',
        header: 'ContentHeader',
        quote: 'ContentQuote',
        image: 'ContentImage',
        carousel: 'ContentCarousel',
        embed: 'ContentEmbed',
        // Also handle common Storyblok component names
        richtext: 'ContentText',
        heading: 'ContentHeader',
        blockquote: 'ContentQuote',
        picture: 'ContentImage',
        gallery: 'ContentCarousel',
        video: 'ContentEmbed',
      };

      const componentName = componentMap[normalizedType];
      if (!componentName) {
        console.warn('Unknown component type:', componentType, 'normalized:', normalizedType);
      }
      return componentName || null;
    },
    getComponentProps(block) {
      if (!block || !block.component) {
        console.warn('Block missing component property:', block);
        return {};
      }

      const component = block.component.toLowerCase().replace(/^church-update-/, '');
      const props = {};

      switch (component) {
        case 'title': {
          const textField = block.text || block.title || block.content || '';
          props.text = convertStoryblokRichTextToHtml(textField);
          props.size = block.size || block.textSize || 'large';
          // Default to rich text (HTML enabled) for all text fields
          props.isHtml = block.isHtml !== undefined ? block.isHtml : true;
          break;
        }

        case 'subtitle': {
          const textField = block.text || block.subtitle || block.content || '';
          props.text = convertStoryblokRichTextToHtml(textField);
          props.size = block.size || block.textSize || 'medium';
          // Default to rich text (HTML enabled) for all text fields
          props.isHtml = block.isHtml !== undefined ? block.isHtml : true;
          break;
        }

        case 'text':
        case 'richtext': {
          const contentField = block.content || block.text || block.body || '';
          props.content = convertStoryblokRichTextToHtml(contentField);
          // Default to rich text (HTML enabled) for all text fields
          props.isHtml = block.isHtml !== undefined ? block.isHtml : true;
          props.size = block.size || block.textSize || 'medium';
          break;
        }

        case 'header':
        case 'heading': {
          const textField = block.text || block.title || block.heading || block.content || '';
          props.text = convertStoryblokRichTextToHtml(textField);
          props.level = block.level || block.headingLevel || block.heading_level || 2;
          // Default to rich text (HTML enabled) for all text fields
          props.isHtml = block.isHtml !== undefined ? block.isHtml : true;
          break;
        }

        case 'quote':
        case 'blockquote': {
          const textField = block.text || block.quote || block.content || '';
          props.text = convertStoryblokRichTextToHtml(textField);
          props.author = block.author || block.authorName || '';
          props.size = block.size || block.textSize || 'medium';
          // Default to rich text (HTML enabled) for all text fields
          props.isHtml = block.isHtml !== undefined ? block.isHtml : true;
          break;
        }

        case 'image':
        case 'picture': {
          // Storyblok image field structure: { filename, alt, title }
          // Prefer an external URL text field (e.g. S3) when present.
          props.imageUrl =
            block.externalUrl?.trim() || this.getImageUrl(block.image || block.picture || block);
          props.altText =
            block.alt || block.altText || block.image?.alt || block.picture?.alt || '';
          props.caption =
            block.name ||
            block.caption ||
            block.image?.name ||
            block.picture?.name ||
            block.image?.title ||
            block.picture?.title ||
            '';
          // Ensure aspectRatio is a string, not an array
          const imageAspectRatio = block.aspectRatio || block.aspect_ratio || 'auto';
          props.aspectRatio = Array.isArray(imageAspectRatio)
            ? imageAspectRatio[0] || 'auto'
            : String(imageAspectRatio);
          break;
        }

        case 'carousel':
        case 'gallery': {
          // Storyblok multi-asset field returns array of images
          props.images = this.normalizeCarouselImages(block);
          // Ensure aspectRatio is a string, not an array
          const carouselAspectRatio = block.aspectRatio || block.aspect_ratio || 'wide';
          props.aspectRatio = Array.isArray(carouselAspectRatio)
            ? carouselAspectRatio[0] || 'wide'
            : String(carouselAspectRatio);
          props.caption = block.caption || '';
          break;
        }

        case 'embed':
        case 'video': {
          // Use url prop - prioritize url, then link, then embedUrl/videoUrl, then code/embedCode/html
          props.url =
            block.url ||
            block.link ||
            block.embedUrl ||
            block.videoUrl ||
            block.code ||
            block.embedCode ||
            block.html ||
            '';

          props.caption = block.caption || block.title || '';
          // Use explicit aspectRatio if provided, otherwise default to 'auto' for auto-detection
          props.aspectRatio = block.aspectRatio || block.aspect_ratio || 'auto';
          break;
        }

        default:
          console.warn('Unknown component type:', component, block);
          return {};
      }

      return props;
    },
    normalizeCarouselImages(block) {
      const images = block.images || block.items || block.gallery || [];
      const baseImages = Array.isArray(images)
        ? images.map(image => this.normalizeCarouselImage(image))
        : [];

      // Optional `imageUrls` text field: newline-separated external/S3 URLs
      // mapped positionally to the items above. Lets a carousel use S3-hosted
      // images (or mix) without consuming Storyblok asset quota. A URL at an
      // index overrides that slide's asset; extra lines add S3-only slides.
      const externalUrls = (block.imageUrls || '').split('\n').map(s => s.trim());
      while (externalUrls.length && !externalUrls[externalUrls.length - 1]) {
        externalUrls.pop();
      }
      if (!externalUrls.length) return baseImages;

      const slideCount = Math.max(baseImages.length, externalUrls.length);
      const mergedImages = [];
      for (let i = 0; i < slideCount; i += 1) {
        const ext = externalUrls[i];
        const base = baseImages[i];
        if (base && typeof base === 'object') {
          mergedImages.push(ext ? { ...base, externalUrl: ext } : base);
        } else if (base) {
          mergedImages.push(ext ? { externalUrl: ext } : base);
        } else if (ext) {
          mergedImages.push({ externalUrl: ext });
        }
      }
      return mergedImages;
    },
    normalizeCarouselImage(imageData) {
      if (!imageData || typeof imageData !== 'object') {
        return imageData;
      }

      const media =
        imageData.image || imageData.picture || imageData.asset || imageData.media || null;
      const caption = this.getCarouselImageCaption(imageData);
      const normalizedImage = {
        ...imageData,
      };

      if (media && !normalizedImage.image) {
        normalizedImage.image = media;
      }

      if (caption) {
        normalizedImage.caption = caption;
      }

      return normalizedImage;
    },
    getCarouselImageCaption(imageData) {
      if (!imageData || typeof imageData !== 'object') return '';

      return (
        imageData.caption ||
        imageData.label ||
        imageData.name ||
        imageData.title ||
        imageData.image?.title ||
        imageData.image?.name ||
        imageData.picture?.title ||
        imageData.picture?.name ||
        imageData.asset?.title ||
        imageData.asset?.name ||
        imageData.media?.title ||
        imageData.media?.name ||
        ''
      );
    },
    getImageUrl(imageData) {
      if (!imageData) return '';

      // Handle string URLs
      if (typeof imageData === 'string') return imageData;

      // Storyblok image structure: { filename: "https://..." } or { filename: "/path/to/image" }
      if (imageData.filename) {
        // Storyblok CDN URLs are already full URLs
        if (imageData.filename.startsWith('http')) {
          return imageData.filename;
        }
        // For relative paths, construct Storyblok CDN URL
        return `https://a.storyblok.com${imageData.filename}`;
      }

      // Fallback to other common structures
      if (imageData.url) return imageData.url;
      if (imageData.image?.filename) {
        const filename = imageData.image.filename;
        return filename.startsWith('http') ? filename : `https://a.storyblok.com${filename}`;
      }

      return '';
    },
    getEditableAttributes(block) {
      return getStoryblokEditableAttributes(block);
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Inter', 'aktiv-grotesk', sans-serif;
}
</style>
